// IE Hub - Counter Field Component
// Counter with increment/decrement buttons

import React, { forwardRef } from 'react';
import {
  Box,
  FormHelperText,
  Typography,
  IconButton,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Remove, Add } from '@mui/icons-material';

interface ValidationRules {
  required?: boolean;
  min?: number;
  max?: number;
  requiredMessage?: string;
}

interface CounterProps {
  label?: string | React.ReactNode;
  value?: number;
  onChange?: (value: number) => void;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  onLimitExceeded?: () => void;
  disableButtonAtLimit?: boolean;
  sx?: Record<string, unknown>;
  [key: string]: any;
}

// Styled counter container
const CounterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: theme.spacing(2),
}));

// Styled counter control
const CounterControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(2),
  padding: theme.spacing(0.5),
  minWidth: 120,
  height: 48,
}));

// Styled counter button
const CounterButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    borderColor: theme.palette.action.disabled,
  },
}));

// Styled counter display
const CounterDisplay = styled(Typography)(({ theme }) => ({
  minWidth: 40,
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
}));

const CounterField = forwardRef<HTMLDivElement, CounterProps>(
  ({
    label,
    value = 0,
    onChange,
    validation,
    onValidate,
    error = false,
    helperText,
    disabled = false,
    min = 0,
    max = 999,
    step = 1,
    onLimitExceeded,
    disableButtonAtLimit = true,
    sx = {},
    ...otherProps
  }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleIncrement = () => {
      if (disabled) return;
      if (value >= max) {
        onLimitExceeded?.();
        validateValue(value);
        return;
      }
      const newValue = Math.min(value + step, max);
      onChange?.(newValue);
      validateValue(newValue);
    };

    const handleDecrement = () => {
      if (disabled) return;
      const newValue = Math.max(value - step, min);
      onChange?.(newValue);
      validateValue(newValue);
    };

    const validateValue = (val: number) => {
      let errorMessage = '';

      if (validation?.required && val === 0) {
        errorMessage = validation.requiredMessage || 'This field is required';
      } else if (validation?.min && val < validation.min) {
        errorMessage = `Value must be at least ${validation.min}`;
      } else if (validation?.max && val > validation.max) {
        errorMessage = `Value must be at most ${validation.max}`;
      }

      onValidate?.(errorMessage === '', errorMessage);
    };

    return (
      <Box ref={ref} sx={{ width: '100%', mb: isMobile ? 2.25 : 2.5, ...sx }} {...otherProps}>
        <CounterContainer>
          {label && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: error ? 'error.main' : 'text.primary',
                flex: 1,
                fontSize: isMobile ? '14px' : '16px',
                lineHeight: isMobile ? '20px' : '22px',
              }}
            >
              {label}
            </Typography>
          )}

          <CounterControl>
            <CounterButton
              onClick={handleDecrement}
              disabled={disabled || value <= min}
              size="small"
            >
              <Remove fontSize="small" />
            </CounterButton>

            <CounterDisplay variant="body1">
              {value}
            </CounterDisplay>

            <CounterButton
              onClick={handleIncrement}
              disabled={disabled || (disableButtonAtLimit && value >= max)}
              size="small"
            >
              <Add fontSize="small" />
            </CounterButton>
          </CounterControl>
        </CounterContainer>

        {helperText && (
          <FormHelperText
            error={error}
            sx={{
              mt: 0.5,
              ml: 0,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }
);

CounterField.displayName = 'CounterField';

export default CounterField;
